/**
 * Findings-queue keyboard map (Linear-style single keys). Pure: the queue
 * component turns a key event into a command here and runs it.
 */

import { isProblemType } from "@iamu/shared";
import type { QueueRowData } from "./queue-types";

export type QueueCommand =
  | "next"
  | "prev"
  | "toggleSelect"
  | "rangeSelect"
  | "open"
  | "close"
  | "accept"
  | "createIssue"
  | "linkIssue"
  | "duplicate"
  | "dismiss"
  | "override"
  | "why"
  | "undo"
  | "search"
  | "help"
  | "goTriage"
  | "goFixNext"
  | "goAwaiting";

export interface KeyInput {
  key: string;
  shiftKey?: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
}

/** The `?` overlay's table: [keys, description]. */
export const QUEUE_KEY_HELP: ReadonlyArray<readonly [string, string]> = [
  ["j / k", "Next / previous finding"],
  ["x", "Select or unselect"],
  ["Shift + x", "Select a range"],
  ["Enter", "Open the detail pane"],
  ["Esc", "Close the pane or clear the selection"],
  ["1", "Accept (needs triage → open)"],
  ["c", "Create issue in tracker (Linear / GitHub)"],
  ["l", "Link existing issue or PR"],
  ["e / 3 / Backspace", "Dismiss"],
  ["2", "Mark duplicate…"],
  ["p", "Pin priority…"],
  ["w", "Why it ranks here"],
  ["z", "Undo the last triage"],
  ["/", "Search"],
  ["g then t / n / r", "Go to Unreviewed / Ready to fix / Awaiting re-run"],
  ["?", "Show this help"],
];

const SINGLE: Record<string, QueueCommand> = {
  j: "next",
  ArrowDown: "next",
  k: "prev",
  ArrowUp: "prev",
  x: "toggleSelect",
  X: "rangeSelect",
  Enter: "open",
  Escape: "close",
  "1": "accept",
  c: "createIssue",
  C: "createIssue",
  l: "linkIssue",
  L: "linkIssue",
  "2": "duplicate",
  "3": "dismiss",
  e: "dismiss",
  Backspace: "dismiss",
  p: "override",
  w: "why",
  z: "undo",
  "/": "search",
  "?": "help",
};

const AFTER_G: Record<string, QueueCommand> = {
  t: "goTriage",
  n: "goFixNext",
  r: "goAwaiting",
};

/**
 * Map a key to a command. `pendingG` is true after a lone `g`; the
 * result says whether the next key completes a `g` chord.
 */
export function commandForKey(
  e: KeyInput,
  pendingG: boolean,
): { command: QueueCommand | null; pendingG: boolean } {
  if (e.altKey || e.ctrlKey) return { command: null, pendingG: false };
  if (e.metaKey) {
    // ⌘Z is undo too; every other ⌘ shortcut belongs to the browser.
    return {
      command: e.key.toLowerCase() === "z" && !e.shiftKey ? "undo" : null,
      pendingG: false,
    };
  }
  if (pendingG) {
    return { command: AFTER_G[e.key] ?? null, pendingG: false };
  }
  if (e.key === "g") return { command: null, pendingG: true };
  if (e.key === "x" && e.shiftKey)
    return { command: "rangeSelect", pendingG: false };
  return { command: SINGLE[e.key] ?? null, pendingG: false };
}

/** Keys never fire while the user types or a dialog/menu owns focus. */
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as Partial<HTMLElement> | null;
  if (!el || typeof el.closest !== "function") return false;
  if (
    el.closest(
      '[data-slot="dialog-content"], [data-slot="sheet-content"], [role="dialog"], [role="menu"], [role="listbox"]',
    )
  ) {
    return true;
  }
  return (
    !!el.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName ?? "")
  );
}

/** Ids from `anchor` to `to` (inclusive) in list order, for Shift+x. */
export function rangeBetween(
  ids: readonly string[],
  anchor: string | null,
  to: string,
): string[] {
  const end = ids.indexOf(to);
  const start = anchor ? ids.indexOf(anchor) : -1;
  if (end < 0) return [];
  if (start < 0) return [to];
  const [a, b] = start <= end ? [start, end] : [end, start];
  return ids.slice(a, b + 1);
}

export type PaneCommand =
  | "accept"
  | "createIssue"
  | "linkIssue"
  | "dismiss"
  | "duplicate"
  | "override";

export interface PaneButtonDef {
  command: PaneCommand;
  label: string;
  key: string;
  when?: (r: QueueRowData) => boolean;
}

export const PANE_BUTTONS: readonly PaneButtonDef[] = [
  {
    command: "accept",
    label: "Accept",
    key: "1",
    when: (r) => r.state === "new",
  },
  {
    command: "createIssue",
    label: "Create issue…",
    key: "c",
    when: (r) => isProblemType(r.type) && r.state !== "dismissed",
  },
  {
    command: "linkIssue",
    label: "Link existing…",
    key: "l",
    when: (r) => isProblemType(r.type) && r.state !== "dismissed",
  },
  {
    command: "dismiss",
    label: "Dismiss",
    key: "e",
    when: (r) => r.state !== "dismissed",
  },
  {
    command: "duplicate",
    label: "Duplicate…",
    key: "2",
    when: (r) => r.state !== "dismissed",
  },
  {
    command: "override",
    label: "Pin priority…",
    key: "p",
    when: (r) => r.priorityScore !== null,
  },
];
